import React from 'react'
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { NexusService } from 'global-apis/nexus-service'
import { useCustomerContext } from '../CustomerContext'
import { useTheme } from '../../../utils/themeContext'
import { getColorsForTheme } from '../../../global-styles/clientColors'

export const RelatedAccounts = () => {
  const { customerId, customerInfo } = useCustomerContext()
  const { currentTheme } = useTheme()
  const themeColors = getColorsForTheme(currentTheme)

  // Fetch list of clients to map client IDs to names
  const { data: clients } = useQuery({
    queryKey: ['clients'],
    queryFn: NexusService.getListClients,
    placeholderData: [],
  })

  // Create a mapping function from client ID to client name
  const getClientName = (clientId: string) => {
    const client = clients?.find((client) => client.id === clientId)
    return client?.name || clientId || 'Unknown'
  }

  // Fetch connected accounts based on phone number
  const { data: relatedAccounts, isLoading, error } = useQuery({
    queryKey: ['connected-accounts', customerInfo?.phone],
    queryFn: async () => {
      if (!customerInfo?.phone) return []

      try {
        const response = await NexusService.getAllCustomersByPhone(customerInfo.phone)
        return response.customersList
          ?.filter((customer) => customer.customerId !== customerId) // Exclude current customer
          ?.map((customer) => ({
            customerId: customer.customerId,
            clientId: customer.clientId || 'Unknown',
            accountNumber: customer.accountNumber || 'N/A',
          })) || []
      } catch (error) {
        console.error('Error fetching connected accounts:', error)
        return []
      }
    },
      enabled: !!customerInfo?.phone,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    }
  )

  // Don't render anything if loading, no phone number, or error occurred
  if (isLoading || !customerInfo?.phone || error) {
    if (error) {
      console.error('Connected accounts error:', error)
    }
    return null
  }

  // Don't render if no connected customers
  if (!relatedAccounts?.length) {
    return null
  }

  return (
    <Card sx={{ mb: 3, p: 2 }}>
      <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
        Related Accounts
      </Typography>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Client Name</TableCell>
              <TableCell>Account Number</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {relatedAccounts.map((customer) => (
              <TableRow key={customer.customerId}>
                <TableCell>{getClientName(customer.clientId)}</TableCell>
                <TableCell>
                  <Link
                    to={`/customers/${customer.customerId}`}
                    style={{
                      color: themeColors.buttonColor,
                      textDecoration: 'none',
                    }}
                  >
                    {customer.accountNumber}
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  )
}
